






































import Vue, { PropOptions } from 'vue';
import validationRequired from '../../utils/validationRules/required';
import { ValidationRule } from '../../types';

// Allow the value to be null - the backend often uses this
const valuePropValidator = (val) => ['string', 'number', 'object'].includes(typeof val) || val === null;

/**
 * A wrapper around the vuetify text input that allows us to set some defaults, limit options and
 * maintain consistency.
 */
export default Vue.extend({
  name: 'SInputSelect',

  props: {
    /**
     * The value of the component
     */
    value: {
      required: true,
      validator: valuePropValidator,
    },

    items: {
      type: Array,
    } as PropOptions<({text: string, value: any} | string)[]>,

    /**
     * Hint text
     */
    hint: {
      type: String,
      default: undefined,
    },

    /**
     * An icon that will be appended to the component
     */
    icon: {
      type: String,
      default: undefined,
    },

    /**
     * The input label
     */
    label: {
      type: String,
      default: undefined,
    },

    /**
     * The name attribute to apply to the input element
     */
    name: {
      type: String,
      default: undefined,
    },

    /**
     * The inputs placeholder text
     */
    placeholder: {
      type: String,
      default: undefined,
    },

    /**
     * If the input is required, uses the required validation rule from the utils
     */
    required: {
      type: Boolean,
      default: false,
    },

    /**
     * An array of validation rules to apply to the component.
     * See `ValidationRule` type in src/types.ts
     */
    rules: {
      type: Array,
      default: (): ValidationRule[] => [],
    } as PropOptions<ValidationRule[]>,

    /**
     * Delays validation until blur event
     */
    validateOnBlur: {
      type: Boolean,
      default: false,
    },

    /**
     * Hides hint and validation errors
     */
    hideDetails: {
      type: Boolean,
      default: false,
    },

    /**
     * Disables the text input
     */
    disabled: {
      type: Boolean,
      default: false,
    },

    /**
     * Enables filled/outlined input style
     */
    filled: {
      type: Boolean,
      default: true,
    },

    /**
     * Shortens the height of the input
     */
    dense: {
      type: Boolean,
      default: false,
    },

    /**
     * Is the input on a dark background? Swaps the colours
     */
    dark: {
      type: Boolean,
      default: false,
    },

    /**
     * Whether or not you can select multiple options.
     */
    multiple: {
      type: Boolean,
      default: false,
    },

    /**
     * Element to attach itself to, the default will set itself to `v-app`.
     */
    attach: {
      type: [String, Boolean],
      default: false,
    },

    menuProps: {
      type: [String, Object],
      default: () => {},
    },
  },

  computed: {
    validationRules(): ValidationRule[] {
      const rules = [...this.rules];

      if (this.required) {
        rules.push(validationRequired);
      }

      return rules;
    },
  },

  beforeDestroy() {
    const emptyValue = typeof this.value === 'string' ? '' : 0;
    this.$emit('input', emptyValue);
  },
});
